import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PaymentModal from './PaymentModal';
import { smartBack } from './lib/navBack';
import { addressLines } from './addressLines';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = 'https://portal.aaairrigationservice.com/header_new.png';
const SEAL_URL = 'https://portal.aaairrigationservice.com/seal.png';

function fmtCurrency(v) {
  return '$' + Number(v || 0).toFixed(2);
}

export default function InvoiceView({ customerId, invoiceId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const params = useParams();

  const custId = customerId || new URLSearchParams(window.location.search).get('customer');
  const invId  = invoiceId  || new URLSearchParams(window.location.search).get('id') || params.invoiceId;

  const load = () => {
    const url = custId
      ? `${BACKEND_URL}/api/customer/${custId}/invoice/${invId}`
      : `${BACKEND_URL}/api/invoice/${invId}`;
    fetch(url)
      .then(r => r.json())
      .then(d => { if (d.data) setDoc(d.data); else setErr('Could not load invoice.'); })
      .catch(() => setErr('Could not load invoice.'));
  };

  useEffect(() => { load(); }, [custId, invId]); // eslint-disable-line

  useEffect(() => {
    if (!invId) return;
    // Portal activity tracking (2026-08-07) — fire-and-forget, must never
    // block rendering or surface an error to the customer.
    fetch(`${BACKEND_URL}/api/track/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_type: 'Sales Invoice', doc_name: invId }),
    }).catch(() => {});
  }, [invId]);

  // /invoice?id=...&customer=... is mailed/texted directly to customers
  // (send_invoice_to_customer, generate_payment_link) and is also opened via
  // window.open() in a new tab from the Dashboard — both leave this tab with
  // no browser history to go "back" to, so window.history.back() alone was a
  // silent no-op. See lib/navBack.js.
  const handleBack = () => {
    if (onClose) { onClose(); return; }
    smartBack(custId ? `/dashboard?customer=${encodeURIComponent(custId)}` : '/dashboard');
  };

  const isPaid = doc && (doc.status === 'Paid' || Number(doc.outstanding_amount) <= 0);
  const items  = doc?.items || [];
  const taxes  = doc?.taxes || [];

  const subtotal = items
    .filter(i => !i.custom_is_subtotal && i.item_code !== 'SUBTOTAL')
    .reduce((s, i) => s + Number(i.amount || 0), 0);
  const taxTotal = taxes.reduce((s, t) => s + Number(t.tax_amount || 0), 0);
  const grandTotal = Number(doc?.grand_total || 0);
  const outstanding = Number(doc?.outstanding_amount || 0);

  if (err) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-red-600 font-medium">{err}</p>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1b7abf] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-container { box-shadow: none !important; margin: 0 !important; }
          @page { size: letter; margin: 0.5in; }
        }
      `}</style>

      {/* Action bar */}
      <div className="no-print sticky top-0 bg-white shadow-md z-40 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1b7abf] transition-colors text-sm font-medium"
          >
            ← Back
          </button>
          <div className="flex gap-3">
            {!isPaid && (
              <button
                onClick={() => setShowPayment(true)}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white uppercase tracking-wider shadow hover:opacity-90"
                style={{ background: '#ea580c' }}
              >
                💳 Pay {fmtCurrency(outstanding)}
              </button>
            )}
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
            >
              🖨 Print
            </button>
            <a
              href={`${BACKEND_URL}/api/customer/${custId}/invoice/${invId}/pdf?session=${encodeURIComponent(localStorage.getItem('portal_token') || '')}`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors no-underline"
            >
              📄 PDF
            </a>
          </div>
        </div>
      </div>

      {/* Invoice document */}
      <div className="py-8 px-4">
        <div className="print-container max-w-4xl mx-auto bg-white shadow-xl rounded-lg overflow-hidden relative">

          {/* First page cover-up for fixed header */}
          <div className="print-first-page-cover" />

          {/* Continuation Header */}
          <div className="continuation-header">
            <span>AAA IRRIGATION SERVICE — INVOICE #{doc.name} (Continuation)</span>
            <span>Page <span className="page-number-continuation" /></span>
          </div>

          {/* Seal watermark */}
          <div
            className="absolute top-1/2 left-1/2 pointer-events-none seal-watermark"
            style={{
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
              opacity: 0.12,
              mixBlendMode: 'multiply',
            }}
          >
            <img src={SEAL_URL} alt="" style={{ width: 520, height: 520 }} />
          </div>

          {/* PAID stamp */}
          {isPaid && (
            <div
              className="absolute top-1/2 left-1/2 pointer-events-none"
              style={{ transform: 'translate(-50%, -50%) rotate(-15deg)', zIndex: 30, opacity: 0.65 }}
            >
              <div className="border-8 border-green-600 rounded-lg px-8 py-4">
                <p className="text-6xl font-black text-green-600 tracking-widest">PAID</p>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="relative p-8" style={{ zIndex: 1 }}>

            {/* Header */}
            <div className="flex items-start justify-between border-b-4 border-[#1b7abf] pb-6 mb-6">
              <div>
                <p className="font-bold text-[#1b7abf] text-lg">AAA IRRIGATION SERVICE, LLC.</p>
                <p className="text-gray-600">14 Monroe Ct.</p>
                <p className="text-gray-600">Allen TX 75002</p>
                <p className="text-gray-600">469 751-3567</p>
                <p className="text-[#1b7abf]">www.aaairrigationservice.com</p>
              </div>
              <div className="flex-1 flex justify-center">
                <img src={LOGO_URL} alt="AAA Irrigation Service" className="h-28 w-auto" />
              </div>
              <div className="text-right">
                <h2 className="text-3xl font-black text-[#1b7abf] tracking-wider uppercase">INVOICE</h2>
              </div>
            </div>

            {/* Customer + Invoice meta */}
            <div className="grid grid-cols-3 gap-6 mb-8 text-sm">
              <div className="space-y-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Bill To</p>
                <p className="text-lg font-bold text-[#1b7abf] leading-snug">{doc.customer_name}</p>
                {doc.address_display && (
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                    {addressLines(doc.address_display)}
                  </p>
                )}
                {doc.contact_mobile && <p className="text-gray-600 mt-1">{doc.contact_mobile}</p>}
                {doc.contact_email  && <p className="text-gray-600 text-xs break-all">{doc.contact_email}</p>}
              </div>

              {/* Middle - Service Address */}
              <div className="space-y-1 text-left">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Service Address</p>
                {doc.shipping_address ? (
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                    {doc.shipping_address.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim()}
                  </p>
                ) : (
                  <p className="text-gray-400 italic">Same as billing</p>
                )}
              </div>

              <div className="text-right">
                <table className="ml-auto text-xs">
                  <tbody>
                    <tr>
                      <td className="text-right text-gray-500 pr-4 py-0.5">Invoice #:</td>
                      <td className="text-left font-bold text-[#1b7abf]">{doc.name}</td>
                    </tr>
                    <tr>
                      <td className="text-right text-gray-500 pr-4 py-0.5">Date:</td>
                      <td className="text-left font-medium">{doc.posting_date}</td>
                    </tr>
                    {doc.due_date && (
                      <tr>
                        <td className="text-right text-gray-500 pr-4 py-0.5">Due:</td>
                        <td className="text-left font-medium">{doc.due_date}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Line items */}
            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1b7abf] text-white">
                    <th className="py-3 px-4 text-left font-semibold">Zone #</th>
                    <th className="py-3 px-4 text-left font-semibold">Description</th>
                    <th className="py-3 px-4 text-center font-semibold">Qty</th>
                    <th className="py-3 px-4 text-right font-semibold">Rate</th>
                    <th className="py-3 px-4 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let running = 0;
                    return items.map((item, idx) => {
                      const isSub = item.custom_is_subtotal || item.item_code === 'SUBTOTAL';
                      if (isSub) {
                        const sectionTotal = running;
                        running = 0;
                        return (
                          <tr key={idx} className="bg-blue-50/40 font-semibold text-[#1b7abf]">
                            <td colSpan={4} className="py-3 px-4 text-right border-b border-blue-200 italic">
                              {(item.item_name && item.item_name.toLowerCase() !== 'subtotal') ? item.item_name : 'Section Subtotal'}
                            </td>
                            <td className="py-3 px-4 text-right border-b border-blue-200">
                              {fmtCurrency(sectionTotal)}
                            </td>
                          </tr>
                        );
                      }
                      running += Number(item.amount || 0);
                      return (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50/40' : 'bg-transparent'}>
                          <td className="py-3 px-4 border-b border-gray-200">{item.custom_zone || ''}</td>
                          <td className="py-3 px-4 border-b border-gray-200">
                            <div>{(item.description || item.item_name || '').replace(/<[^>]+>/g, '').trim()}</div>
                            {(item.custom_before_photo_url || item.custom_after_photo_url) && (
                              <div className="mt-2 flex gap-2 no-print">
                                {item.custom_before_photo_url && (
                                  <div className="flex flex-col items-center">
                                    <span className="text-[10px] text-gray-400 font-medium">Before</span>
                                    <a href={item.custom_before_photo_url} target="_blank" rel="noreferrer">
                                      <img
                                        src={item.custom_before_photo_url}
                                        alt="Before service"
                                        className="w-10 h-10 object-cover rounded border border-gray-200 hover:opacity-80 transition-opacity"
                                      />
                                    </a>
                                  </div>
                                )}
                                {item.custom_after_photo_url && (
                                  <div className="flex flex-col items-center">
                                    <span className="text-[10px] text-gray-400 font-medium">After</span>
                                    <a href={item.custom_after_photo_url} target="_blank" rel="noreferrer">
                                      <img
                                        src={item.custom_after_photo_url}
                                        alt="After service"
                                        className="w-10 h-10 object-cover rounded border border-gray-200 hover:opacity-80 transition-opacity"
                                      />
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 border-b border-gray-200 text-center">{item.qty}</td>
                          <td className="py-3 px-4 border-b border-gray-200 text-right">{fmtCurrency(item.rate)}</td>
                          <td className="py-3 px-4 border-b border-gray-200 text-right font-medium">{fmtCurrency(item.amount)}</td>
                        </tr>
                      );
                    });
                  })()}
                  {/* pad to 5 rows minimum */}
                  {Array.from({ length: Math.max(0, 5 - items.length) }).map((_, i) => (
                    <tr key={`pad-${i}`} className={items.length + i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-3 px-4 border-b border-gray-200">&nbsp;</td>
                      <td className="py-3 px-4 border-b border-gray-200">&nbsp;</td>
                      <td className="py-3 px-4 border-b border-gray-200">&nbsp;</td>
                      <td className="py-3 px-4 border-b border-gray-200">&nbsp;</td>
                      <td className="py-3 px-4 border-b border-gray-200">&nbsp;</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals & Notes */}
            <div className="flex justify-between items-start mb-8 gap-8">
              <div className="flex-1 min-w-0">
                {doc.custom_ai_summary && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-left">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Job Summary</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {doc.custom_ai_summary.replace(/<[^>]+>/g, '').trim()}
                    </p>
                  </div>
                )}
              </div>
              <div className="w-72 shrink-0">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{fmtCurrency(subtotal)}</span>
                </div>
                {taxes.map((t, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">{t.description || 'Tax'}:</span>
                    <span className="font-medium">{fmtCurrency(t.tax_amount)}</span>
                  </div>
                ))}
                {taxes.length === 0 && taxTotal === 0 && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Tax:</span>
                    <span className="font-medium">$0.00</span>
                  </div>
                )}
                {Number(doc?.discount_amount || 0) > 0 && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Discount:</span>
                    <span className="font-medium text-red-500">-{fmtCurrency(doc.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between py-3 text-white px-4 rounded-lg mt-2" style={{ background: '#1b7abf' }}>
                  <span className="font-bold text-lg">TOTAL:</span>
                  <span className="font-bold text-lg">{fmtCurrency(grandTotal)}</span>
                </div>
                {!isPaid && outstanding < grandTotal && (
                  <div className="flex justify-between py-2 mt-1">
                    <span className="text-gray-600 font-medium">Balance Due:</span>
                    <span className="font-bold text-[#ea580c]">{fmtCurrency(outstanding)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment buttons */}
            {!isPaid && (
              <div className="no-print flex justify-center gap-4 mb-8 py-4 border-t border-b border-gray-200">
                <button
                  onClick={() => setShowPayment(true)}
                  className="flex items-center gap-2 px-6 py-3 text-white rounded-xl shadow-lg hover:opacity-90 transition font-bold"
                  style={{ background: '#ea580c' }}
                >
                  💳 Pay with Credit Card
                </button>
                <button
                  onClick={() => setShowPayment(true)}
                  className="flex items-center gap-2 px-6 py-3 text-white rounded-xl shadow-lg hover:opacity-90 transition font-bold"
                  style={{ background: '#6D1ED4' }}
                >
                  ⚡ Pay with Zelle
                </button>
              </div>
            )}

            {/* Footer */}
            <div className="text-center pt-4 border-t border-gray-300 print-footer">
              <p className="text-xs text-gray-500 italic">
                Irrigation in Texas is regulated by the TCEQ, P.O. Box 13087, Austin, Texas 78711-3087
              </p>
              <p className="text-xs font-semibold mt-0.5" style={{ color: '#00cc44' }}>
                Powered by Boatman Systems™
              </p>
              <div className="print-only-page-number text-xs text-gray-400 mt-1" />
            </div>
          </div>
        </div>
      </div>

      {showPayment && (
        <PaymentModal
          invoice={{ name: doc.name, outstanding_amount: doc.outstanding_amount, customer_email: doc.contact_email || '', customer_name: doc.customer_name || '' }}
          backendUrl={BACKEND_URL}
          onClose={() => setShowPayment(false)}
          onSuccess={() => { setShowPayment(false); load(); }}
        />
      )}
    </div>
  );
}
